import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { H_PLUVIO_OPTIONS } from '../../../account/constants/AccessRulesConstants'
import StationAction from '../../../station/actions/StationAction'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { getSetting, getSettingInt } from '../../../utils/SettingUtils'
import AdministrationAction from '../../actions/AdministrationAction'
import DataTypesTable from '../dataTypes/DataTypesTable'
import ShowAssociatedDataRuleTable from '../dataTypes/ShowAssociatedDataRuleTable'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import Select from '../../../components/forms/Select'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { darkBlue } from 'utils/constants/ColorTheme'
import { omitBy, isObject } from 'lodash'

const project = 'SIP'

const AdminPluviometerApp = () => {
    const {
        applicationSettingsStore,
        contributors,
        filteredDataTypes,
    } = useSelector(store => ({
        applicationSettingsStore: store.AdministrationReducer.applicationSettings,
        contributors: store.ContributorReducer.contributors,
        filteredDataTypes: store.StationReducer.filteredDataTypes,
    }), shallowEqual)

    const [readMode, setReadMode] = useState(true)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [applicationSettings, setApplicationSettings] = useState(applicationSettingsStore)
    const [validationDefaultProducerPluvio, setValidationDefaultProducerPluvio] = useState(getSettingInt(applicationSettingsStore, 'validationDefaultProducerPluvio'))
    const [validationDefaultManagerPluvio, setValidationDefaultManagerPluvio] = useState(getSettingInt(applicationSettingsStore, 'validationDefaultManagerPluvio'))
    const [validationDefaultValidatorPluvio, setValidationDefaultValidatorPluvio] = useState(getSettingInt(applicationSettingsStore, 'validationDefaultValidatorPluvio'))
    const [showDataTypesRules, setShowDataTypesRules] = useState(getSetting(applicationSettingsStore, 'showDataTypesRules'))
    const [dataTypes, setDataTypesState] = useState([])

    const dispatch = useDispatch()

    const setDataTypes = () => {
        dispatch(StationAction.fetchDataTypesByProject(project)).then((res) => {
            setDataLoaded(true)
            setDataTypesState(res)
        })
    }

    useEffect(() => {
        if (!componentHasHabilitations(H_PLUVIO_OPTIONS)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return
        }
        dispatch(ContributorAction.fetchContributors())
        setDataTypes()
    }, [])

    useTitle(() => [{
        title: i18n.pluviometry,
        href: 'pluviometry',
    }, {
        title: i18n.pluviometryOptions,
        href: 'pluviometry/pluviometryOptions',
    }], [])

    const onCancel = () => {
        setDataTypes(filteredDataTypes)
        setApplicationSettings(applicationSettingsStore)
        setReadMode(true)
    }

    const onSave = () => {
        setDataLoaded(false)
        dispatch(StationAction.updateDataTypesByProject(project, dataTypes.map(d => omitBy(d, isObject)))).then(() => {
            setDataTypes()
        })

        const newSetting = [
            { parameter: 'validationDefaultProducerPluvio', value: validationDefaultProducerPluvio ? validationDefaultProducerPluvio.toString() : validationDefaultProducerPluvio },
            { parameter: 'validationDefaultManagerPluvio', value: validationDefaultManagerPluvio ? validationDefaultManagerPluvio.toString() : validationDefaultManagerPluvio },
            { parameter: 'validationDefaultValidatorPluvio', value: validationDefaultValidatorPluvio ? validationDefaultValidatorPluvio.toString() : validationDefaultValidatorPluvio },
            { parameter: 'showDataTypesRules', value: showDataTypesRules },
        ].filter(p => getSetting(applicationSettings, p.parameter) !== p.value)

        if (applicationSettings.length) {
            dispatch(AdministrationAction.updateSieauParameters(newSetting))
        }
        setReadMode(true)
    }

    useActions(() => {
        if (readMode) {
            return {
                edit: () => setReadMode(false),
            }
        }
        return {
            save: () => onSave(),
            cancel: () => onCancel(),
        }
    }, [readMode, applicationSettings, applicationSettingsStore, dataTypes])

    return (
        <Stack spacing={2} padding={1}>
            <Card>
                <CardTitle title={i18n.pluviometryOptions} color={darkBlue} />
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <h5>{ i18n.validationScreenDefaultValues }</h5>
                        </Grid>
                        <Grid size={4}>
                            <Select
                                disabled={readMode}
                                label={ i18n.producer }
                                options={ contributors }
                                value={ validationDefaultProducerPluvio }
                                onChange={ v => setValidationDefaultProducerPluvio(v) }
                                keyValue='id'
                                keyLabel='mnemonique'
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select
                                disabled={readMode}
                                label={ i18n.administrator }
                                options={ contributors }
                                value={ validationDefaultManagerPluvio }
                                onChange={ v => setValidationDefaultManagerPluvio(v) }
                                keyValue='id'
                                keyLabel='mnemonique'
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select
                                disabled={readMode}
                                label={ i18n.validator }
                                options={ contributors }
                                value={ validationDefaultValidatorPluvio }
                                onChange={ v => setValidationDefaultValidatorPluvio(v) }
                                keyValue='id'
                                keyLabel='mnemonique'
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {dataLoaded && (
                <DataTypesTable
                    project={project}
                    dataTypes={dataTypes}
                    readMode={readMode}
                    onChange={setDataTypesState}
                    style={{ marginTop: '1rem' }}
                />
            )}
            {dataLoaded && (
                <ShowAssociatedDataRuleTable
                    currentSettings={[{ parameter: 'showDataTypesRules', value: showDataTypesRules }]}
                    fromStationType='PLUVIO'
                    toStationType='PIEZO'
                    onChangeRules={ v => setShowDataTypesRules(v) }
                    readMode={readMode}
                />
            )}
            {dataLoaded && (
                <ShowAssociatedDataRuleTable
                    currentSettings={[{ parameter: 'showDataTypesRules', value: showDataTypesRules }]}
                    fromStationType='PLUVIO'
                    toStationType='HYDRO'
                    onChangeRules={ v => setShowDataTypesRules(v) }
                    readMode={readMode}
                />
            )}
        </Stack>
    )
}

export default AdminPluviometerApp